/**
 * PB-Detail · Left sidebar (sticky, always visible across all tabs).
 *
 * Bundles identity, assignment, core meta, role summary, PB-quick facts,
 * free-text description and contacts — mirroring the CRM 1.0 sidebar
 * (`crm1.0/sh24-frontend/src/screens/User.js` lines 370–610) but rebuilt
 * against the CRM 2.0 design system.
 *
 * Read-only by default: edits delegate to the Profildaten tab or to inline
 * modals (Settings, Sterbedatum, Assign-Admin) — those live in PflegePage
 * for now.
 *
 * feature/pb-phase-process: Phase Stepper added below the identity card.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Card } from '../../../components/ui';
import { BRAND, BRAND_LIGHT, BRAND_DARK, getThemeColors } from '../../../lib/theme';
import { type V1User, type V1Profile, type V1Admin, primaryProfile, selectableAdmins, v1Admins, v1Users } from '../../../lib/v1';
import { HARDCODED } from './options';
import { displayPflegegradLevel } from './fields';
import { Rating } from './controls';
import { useOptionLabel } from '../../../lib/useOptionLabel';
import { formatDate } from '../../../lib/dateFormat';
import { DateInput } from '../../../components/DateInput';
import { PbMapWidget } from '../PbMapWidget';
import { SetcardSharePanel } from './SetcardSharePanel';
import { PhoneLink } from '../../../components/PhoneLink';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { deriveSectionPerms } from './sections';

interface PbSidebarProps {
  user: V1User | null;
  dark: boolean;
  /**
   * Ist die Person ein Pflegeempfänger? Bei `false` (reine Kontaktperson:
   * Angehörige, Rechnungsempfänger, Hausarzt) entfallen die Blöcke, die nur
   * für Betreute Sinn ergeben: Personenangaben (Geburtsdatum/Größe/Gewicht),
   * Startdatum und Nettolohn. Der Rest der Leiste bleibt identisch, damit
   * Kontakt- und PB-Profil denselben Aufbau haben.
   */
  careRecipient?: boolean;
  /**
   * Wird nach einem Inline-Save (z. B. Startdatum) mit dem aktualisierten
   * User aufgerufen, damit Elternkomponenten (und das Profildaten-Formular)
   * den neuen Stand übernehmen.
   */
  onSaved?: (u: V1User) => void;
}

/** ISO-/DB-Datum → `YYYY-MM-DD` für `<DateInput>`; sonst ''. */
export function toDateInputValue(v: unknown): string {
  if (v == null || v === '') return '';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

export function PbSidebar({ user, dark, careRecipient = true, onSaved }: PbSidebarProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const optLabel = useOptionLabel();
  const profile: V1Profile | null = user ? primaryProfile(user) : null;
  const pgLevel = displayPflegegradLevel(
    profile as Record<string, unknown> | null,
    user as unknown as Record<string, unknown> | null,
  );

  // ── Zugewiesen an (assigned admin) — editable inline in the identity card ──
  const [admins, setAdmins] = useState<V1Admin[]>([]);
  const [assignedTo, setAssignedTo] = useState<number | null>(
    user?.assigned_to != null ? Number(user.assigned_to) : null,
  );
  const [savingAssign, setSavingAssign] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    v1Admins.list()
      .then(list => { if (alive) setAdmins(list); })
      .catch(() => {/* field degrades to id-only display */});
    return () => { alive = false; };
  }, []);

  const assignedAdmin = useMemo(
    () => admins.find(a => a.id === assignedTo) ?? null,
    [admins, assignedTo],
  );
  const adminName = (a: V1Admin): string =>
    [a.vorname, a.nachname].filter(Boolean).join(' ') || a.email || tr('pflege.dashboard.admin_fallback', { id: a.id });

  const handleAssign = async (next: number | null) => {
    if (!user?.id || savingAssign) return;
    const prev = assignedTo;
    setAssignedTo(next);          // optimistic
    setSavingAssign(true);
    setAssignError(null);
    try {
      // Befund 23.09.: v2-Endpunkt statt v1Users.update() — spiegelt
      // serverseitig zusätzlich auf family_accounts.assigned_to +
      // Geschwister-Anfragen (AssignedToSyncService), sonst driftet die
      // Zuständigkeit zwischen Profil-Sidebar, /anfragen-Liste und
      // Familienkonto auseinander.
      await v1Users.setAssignedTo(user.id, next);
    } catch (e) {
      setAssignedTo(prev);        // rollback
      setAssignError(e instanceof Error ? e.message : tr('pflege.detail.assign_error'));
    } finally {
      setSavingAssign(false);
    }
  };

  // ── Startdatum — inline editierbar (Rechte wie die Status-Sektion) ──
  const { hasPermission } = usePermissions();
  const canWriteStatus = deriveSectionPerms(hasPermission).status.canWrite;
  const [startdatum, setStartdatum] = useState<string>(toDateInputValue(user?.startdatum));
  const [savingStart, setSavingStart] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  // Server-/Elternstand übernehmen (z. B. nach Save im Profildaten-Tab).
  useEffect(() => { setStartdatum(toDateInputValue(user?.startdatum)); }, [user?.startdatum]);

  const handleStartdatum = async (next: string) => {
    if (!user?.id || savingStart) return;
    const prev = startdatum;
    if (next === prev) return;
    setStartdatum(next);          // optimistic
    setSavingStart(true);
    setStartError(null);
    try {
      // Gleicher Speicherpfad wie der Profildaten-Save (PUT /v1/admin/user/{id}).
      await v1Users.update(user.id, { startdatum: next || null });
      onSaved?.({ ...user, startdatum: next || null } as V1User);
    } catch (e) {
      setStartdatum(prev);        // rollback
      setStartError(e instanceof Error ? e.message : tr('pflege.edit.save_failed'));
    } finally {
      setSavingStart(false);
    }
  };

  const initials = useMemo(() => {
    const v = String(profile?.vorname ?? '')[0] ?? '';
    const n = String(profile?.nachname ?? '')[0] ?? '';
    // Als „Name" an <Avatar> — zwei Buchstaben, getrennt, damit Avatar sie übernimmt.
    return [v, n].filter(Boolean).join(' ').toUpperCase() || 'P B';
  }, [profile?.vorname, profile?.nachname]);

  const name = [profile?.titel, profile?.vorname, profile?.nachname]
    .filter(Boolean).join(' ') || tr('pflege.detail.new_profile');

  const age = useMemo(() => {
    if (!profile?.geburtsdatum) return null;
    const d = new Date(profile.geburtsdatum);
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 31557600000);
  }, [profile?.geburtsdatum]);

  const statusLabel = lookupHardcoded('status', String(user?.status ?? '')) ?? 'Ausstehend';
  const statusTone = statusLabel === 'Aktiv'
    ? { bg: dark ? '#0f3a23' : '#ecfdf3', color: dark ? '#86efac' : '#027a48' }
    : statusLabel === 'Pausiert'
      ? { bg: dark ? '#1a2a3a' : '#eff8ff', color: dark ? '#7dd3fc' : '#026aa2' }
      : { bg: dark ? '#3a2a0f' : '#fffaeb', color: dark ? '#fcd34d' : '#b54708' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Identity card */}
      <Card dark={dark} style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {/* Feedback 11.10.2026: Profilbild (Foto „Person"), sonst Initialen. */}
          <Avatar name={initials} userId={user?.id ?? null} size={56} dark={dark} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: t.heading, lineHeight: 1.2 }}>{name}</div>
              {/* User-ID statt Familienkonto (Nacharbeit 02.10.): dezenter Chip
                  direkt neben dem Namen. */}
              {user?.id != null && (
                <span
                  data-testid="pb-sidebar-user-id"
                  title={tr('pflege.sidebar.user_id_hint')}
                  style={{
                    display: 'inline-flex', alignItems: 'center', flexShrink: 0,
                    fontSize: 11, fontWeight: 600, color: t.muted,
                    background: t.chipBg, border: `1px solid ${t.border}`,
                    borderRadius: 999, padding: '2px 8px', lineHeight: 1.4,
                    fontFamily: 'Outfit, sans-serif', userSelect: 'all',
                  }}
                >
                  {tr('pflege.sidebar.user_id_label', { id: user.id })}
                </span>
              )}
            </div>
            {/* T4: Bewertung direkt unter dem Namen neben „Pflegebedürftige:r" */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <span style={{ fontSize: 12, color: t.muted }}>{tr('pflege.cares.th.care_recipient')}</span>
              <Rating dark={dark} value={Number(user?.rating ?? 0)} />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            background: statusTone.bg, color: statusTone.color,
            borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600,
          }}>{optLabel(statusLabel)}</span>
          {/* A11: eine Quelle für die Pflegegrad-Pill (pflegegrad_level, sonst
              aus der profile_options-ID 5–9 abgeleitet). Nur für
              Pflegeempfänger; „kein PG" statt leer. */}
          {careRecipient && (
            <span
              data-testid="pflegegrad-pill"
              style={{
                background: dark ? '#2a1222' : BRAND_LIGHT,
                color: dark ? '#f49dd1' : BRAND_DARK,
                borderRadius: 999, padding: '3px 10px', fontSize: 11.5, fontWeight: 600,
              }}
            >{pgLevel != null ? `PG ${pgLevel}` : tr('pflege.sidebar.no_pg')}</span>
          )}
        </div>

        {/* Zugewiesen an — assigned admin with avatar + inline reassign */}
        {user?.id != null && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${t.borderSoft}` }}>
            <div style={{
              fontSize: 11, fontWeight: 700, color: t.muted,
              textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
            }}>{tr('pflege.form.field.assigned_to')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {assignedAdmin
                ? <Avatar name={adminName(assignedAdmin)} adminId={assignedAdmin.id} size={34} tone="blue" dark={dark} />
                : <div style={{
                    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                    background: t.chipBg, color: t.muted,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                  }}>—</div>}
              <select
                aria-label={tr('pflege.form.field.assigned_to')}
                value={assignedTo ?? ''}
                disabled={savingAssign}
                onChange={e => handleAssign(e.target.value ? Number(e.target.value) : null)}
                style={{
                  flex: 1, minWidth: 0, fontSize: 13, fontFamily: 'Outfit,sans-serif',
                  border: `1px solid ${t.border}`, borderRadius: 8, padding: '7px 9px',
                  background: t.inputBg, color: t.text, cursor: savingAssign ? 'wait' : 'pointer',
                }}
              >
                <option value="">{tr('pflege.form.ph.assigned_to')}</option>
                {selectableAdmins(admins, [assignedTo]).map(a => (
                  <option key={a.id} value={a.id}>{adminName(a)}</option>
                ))}
              </select>
            </div>
            {assignError && (
              <div style={{ marginTop: 6, fontSize: 11.5, color: '#b42318' }}>{assignError}</div>
            )}
          </div>
        )}
      </Card>

      {/* Core meta rows — Phase (→ PbPhaseBar oben), User-ID (→ Breadcrumb-Copy-Link)
          und Bewertung (→ Identity-Card) wurden bewusst hier entfernt.
          Startdatum ist ein Betreuungs-Stammdatum: bei reinen Kontaktpersonen
          entfällt die Karte, wenn nur es darin stünde. Der Nettolohn wurde
          mit Paket A (A1) aus der Sidebar entfernt; er bleibt in der
          Vertrag-Sektion der Profildaten. */}
      {(careRecipient || strOr(user?.todestag)) && (
        <Card dark={dark} style={{ padding: 0 }}>
          {careRecipient && (
            canWriteStatus && user?.id != null ? (
              <Row dark={dark} label={tr('pflege.edit.header.startdatum')}>
                <DateInput
                  data-testid="pb-sidebar-startdatum"
                  ariaLabel={tr('pflege.edit.header.startdatum')}
                  value={startdatum}
                  disabled={savingStart}
                  // DateInput meldet nur vollständige, gültige Daten (oder "") → direkt speichern.
                  onChange={v => { void handleStartdatum(v); }}
                  style={{
                    fontSize: 13, fontFamily: 'Outfit,sans-serif',
                    border: `1px solid ${t.border}`, borderRadius: 8, padding: '4px 8px',
                    background: t.inputBg, color: t.text, cursor: savingStart ? 'wait' : undefined,
                    width: 140, maxWidth: '100%', boxSizing: 'border-box',
                  }}
                />
              </Row>
            ) : (
              <Row dark={dark} label={tr('pflege.edit.header.startdatum')}>{fmtDate(user?.startdatum)}</Row>
            )
          )}
          {careRecipient && startError && (
            <div role="alert" style={{ padding: '6px 16px', fontSize: 11.5, color: '#b42318' }}>{startError}</div>
          )}
          {strOr(user?.todestag) ? (
            <Row dark={dark} label={tr('pflege.sidebar.death_day')} tone="warn">{fmtDate(user!.todestag)}</Row>
          ) : null}
        </Card>
      )}

      {/* Setcard (einklappbar) — nach dem Startdatum; ansehen, PDF und öffentlicher PIN-geschützter Link */}
      {user?.id != null && (
        <SetcardSharePanel userId={user.id} dark={dark} />
      )}

      {/* Personenangaben (Geburtsdatum/Größe/Gewicht) sind Pflege-Stammdaten
          und entfallen bei reinen Kontaktpersonen. Die Erreichbarkeits-Zeilen
          bleiben — für eine Kontaktperson ist die Rufnummer die zentrale
          Information; die Karte heißt dann „Kontakt". */}
      {profile && (
        <Card dark={dark} style={{ padding: 0 }}>
          <SectionHead dark={dark}>
            {careRecipient ? tr('pflege.sidebar.section_person') : tr('pflege.detail.section.contact')}
          </SectionHead>
          {careRecipient && profile.geburtsdatum && (
            <Row dark={dark} label={tr('pflege.form.field.geburtsdatum')}>{fmtDate(profile.geburtsdatum)}{age != null ? ` · ${age} ${tr('pflege.sidebar.years_abbr')}` : ''}</Row>
          )}
          {careRecipient && profile.groesse != null && <Row dark={dark} label={tr('pflege.form.field.groesse')}>{String(profile.groesse)} cm</Row>}
          {careRecipient && profile.gewicht != null && <Row dark={dark} label={tr('pflege.form.field.gewicht')}>{String(profile.gewicht)} kg</Row>}
          {strOr(user?.telefon) && <Row dark={dark} label={tr('pflege.form.field.telefon')}><PhoneLink number={strOr(user!.telefon)} dark={dark} /></Row>}
          {user?.mobile && <Row dark={dark} label={tr('pflege.form.field.mobile')}><PhoneLink number={user.mobile} dark={dark} /></Row>}
          {user?.email && <Row dark={dark} label={tr('pflege.form.field.email')}>{user.email}</Row>}
        </Card>
      )}

      {/* Standort — eigene Karte (K1). Die Anschrift steht im Footer der Karte
          (PbMapWidget); keine separate Anschrift-Card mehr. */}
      {(strOr(user?.strasse) || strOr(user?.ort) || strOr(profile?.strasse) || strOr(profile?.ort)) && (
        <PbMapWidget user={user} dark={dark} mode="compact" />
      )}

      {/* Description */}
      {strOr(user?.description) ? (
        <Card dark={dark} style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
            {tr('pflege.sidebar.freetext')}
          </div>
          <div style={{ fontSize: 13, color: t.text, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {strOr(user!.description)}
          </div>
        </Card>
      ) : null}

    </div>
  );
}

/* ─── helpers ──────────────────────────────────────────────── */

function Row({
  label, children, dark, tone,
}: {
  label: string;
  children: React.ReactNode;
  dark: boolean;
  tone?: 'warn';
}) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      gap: 12, padding: '10px 16px',
      borderBottom: `1px solid ${t.borderSoft}`,
      fontSize: 13,
    }}>
      <span style={{ color: t.muted, fontWeight: 500 }}>{label}</span>
      <span style={{
        color: tone === 'warn' ? '#b42318' : t.text,
        fontWeight: 500, textAlign: 'right',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: '60%',
      }}>{children}</span>
    </div>
  );
}

function SectionHead({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  return (
    <div style={{
      padding: '12px 16px 6px',
      fontSize: 11, fontWeight: 700, color: t.muted,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      borderBottom: `1px solid ${t.borderSoft}`,
    }}>{children}</div>
  );
}

/** Returns the string form of `v` if it's a non-empty primitive, otherwise '' (which is falsy). */
function strOr(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'object') return '';
  return String(v);
}

function fmtDate(v: unknown): string {
  if (v == null || v === '') return '—';
  const s = String(v);
  // Already a German date? Pass through.
  if (/^\d{2}\.\d{2}\.\d{4}/.test(s)) return s.slice(0, 10);
  // ISO date / datetime → DE („26.10.2026"); Ungültiges roh anzeigen.
  return formatDate(s, s);
}

function lookupHardcoded(key: 'status' | 'konto' | 'badge' | 'impfstatus' | 'locale' | 'land', value: string): string | null {
  const list = HARDCODED[key];
  if (!list) return null;
  const match = (list as Array<string | { value: string; label: string }>).find(o => {
    if (typeof o === 'string') return o === value;
    return o.value === value;
  });
  return match ? (typeof match === 'string' ? match : match.label) : null;
}
